import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { healthCommissionService, cacheHealthCommission } from '../Services/HealthCommissionService';

const MAX_LOGO_BYTES = 500 * 1024;
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';

// Template Settings card: health commission registration no. + logo, printed
// below the doctor info at the bottom right of every report.
// See docs/features/health-commission/spec.md.
export default function HealthCommissionSettings() {
  const [registrationNo, setRegistrationNo] = useState('');
  const [saved, setSaved] = useState(null); // last saved { logo, logoContentType }
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    healthCommissionService
      .get()
      .then((data) => {
        setRegistrationNo(data?.registrationNo || '');
        setSaved(data);
        cacheHealthCommission(data);
      })
      .catch((error) => {
        console.error('Failed to load health commission settings:', error);
      });
  }, []);

  const clearFile = () => {
    setFile(null);
    setFilePreview(null);
    setFileInputKey((k) => k + 1);
  };

  const onFileChange = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return clearFile();
    if (!chosen.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return clearFile();
    }
    if (chosen.size > MAX_LOGO_BYTES) {
      toast.error('Logo must be 500 KB or smaller');
      return clearFile();
    }
    setFile(chosen);
    const reader = new FileReader();
    reader.onload = () => setFilePreview(reader.result);
    reader.readAsDataURL(chosen);
  };

  const save = async (removeLogo = false) => {
    setSaving(true);
    try {
      const data = await healthCommissionService.save(registrationNo, removeLogo ? null : file, removeLogo);
      setSaved(data);
      setRegistrationNo(data?.registrationNo || '');
      cacheHealthCommission(data);
      clearFile();
      toast.success(removeLogo ? 'Health commission logo removed' : 'Health commission settings saved');
    } catch (error) {
      const message = typeof error?.response?.data === 'string' ? error.response.data : 'Failed to save health commission settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const logoSrc = filePreview || (saved?.logo ? `data:${saved.logoContentType || 'image/png'};base64,${saved.logo}` : null);

  return (
    <div className='common-box-shadow common-bg-white common-br-3px p-3 mb-3'>
      <h6 className="common-FontSize-small common-FontWeight mb-1">
        Health Commission <span className="text-muted fw-normal">(Shown below doctor info, bottom right of every report)</span>
      </h6>
      <hr className="my-2" />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
      >
        <div className="row g-3 align-items-start">
          <div className="col-12 col-md-5">
            <label className='common-Labels d-block mb-1' htmlFor="hcRegistrationNo">Registration Number</label>
            <input
              id="hcRegistrationNo"
              className="form-control common-Input"
              value={registrationNo}
              maxLength={100}
              onChange={(e) => setRegistrationNo(e.target.value)}
              placeholder="e.g. KPHCC-12345"
            />

            <label className='common-Labels d-block mb-1 mt-2' htmlFor="hcLogo">Logo Image</label>
            <input
              key={fileInputKey}
              id="hcLogo"
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={onFileChange}
              className="form-control form-control-sm common-Input"
            />
            <span className="common-FontSize-small text-muted d-block mt-1" style={{ fontSize: '11px' }}>
              PNG, JPEG, GIF, WebP or SVG, up to 500 KB.
            </span>
          </div>

          <div className="col-12 col-md-4">
            <label className='common-Labels d-block mb-1'>Report Preview</label>
            <div
              className="border common-br-3px p-2 d-flex flex-column align-items-end justify-content-center"
              style={{ background: '#f8f9fa', minHeight: '90px', textAlign: 'right' }}
            >
              {logoSrc ? (
                <img src={logoSrc} alt="Health Commission logo" style={{ maxHeight: '50px', maxWidth: '140px', objectFit: 'contain' }} />
              ) : (
                <span className="text-muted" style={{ fontSize: '11px' }}>No logo</span>
              )}
              <div style={{ fontSize: '10px', marginTop: '4px' }}>
                <span style={{ fontWeight: 600 }}>Reg.No:</span> {registrationNo.trim() || '-'}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <Button type='submit' variant="contained" className="common-button ms-0" disabled={saving}>
            {saving ? 'Saving...' : 'Save Health Commission'}
          </Button>
          {saved?.logo && !file && (
            <Button type='button' variant="outlined" color="error" disabled={saving} onClick={() => save(true)}>
              Remove Logo
            </Button>
          )}
          {file && (
            <Button type='button' variant="outlined" disabled={saving} onClick={clearFile}>
              Cancel New Logo
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
